import React from 'react';
import {
    useTheme,
    Box,
    Typography,
    Checkbox,
    FormControlLabel,
    Chip,
    Divider,
} from '@mui/material';
import FlexBetween from 'components/FlexBetween';


const SetPermissionList = ({
    permissions = [],
    selectedRights,
    setSelectedRights,
    rowSelectionModel,
    setRowSelectionModel,
}) => {
    const theme = useTheme();

    const toggleRow = (permId, checked) => {
        setRowSelectionModel(prev => {
            const newIds = new Set(prev.ids);
            if (checked) newIds.add(permId);
            else newIds.delete(permId);
            return { type: 'include', ids: newIds };
        });
    };

    const toggleRight = (permId, rightName, checked) => {
        setSelectedRights(prev => ({
            ...prev,
            [permId]: { ...prev[permId], [rightName]: checked },
        }));
        setRowSelectionModel(prev => {
            if (prev.ids.has(permId)) return prev;
            return { type: 'include', ids: new Set([...prev.ids, permId]) };
        });
    };


    return (
        <Box 
            display="flex" 
            flexDirection="column" 
            gap={2}
        >
            {permissions.map((perm, index) => {
                const permId = perm._id;
                const isSelected = rowSelectionModel.ids.has(permId);
                const rights = perm.right || [];

                return (
                    <Box
                        key={permId || index}
                        sx={{
                            p: 1.5,
                            borderRadius: 2,
                            border: `1px solid ${isSelected ? theme.palette.primary.main : theme.palette.neutral.deep}`,
                            backgroundColor: isSelected ? theme.palette.action.selected : 'background.paper',
                            transition: 'all 0.15s ease-in-out',
                        }}
                    >
                        <FlexBetween>
                            <Box display="flex" alignItems="center" gap={0.5}>
                                <Checkbox
                                    size="small"
                                    checked={isSelected}
                                    onChange={(e) => toggleRow(permId, e.target.checked)}
                                    sx={{ p: 0.5 }}
                                />
                                <Typography variant="body2" fontWeight={600}>
                                    {perm.element}
                                </Typography>
                            </Box>
                            <Chip
                                label={perm.category || '—'}
                                size="small"
                                variant="outlined"
                                sx={{ height: 20, fontSize: '0.7rem' }}
                            />
                        </FlexBetween>

                        {perm.path && (
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                display="block"
                                sx={{ pl: 3.7, mb: 0.5 }}
                            >
                                Path: {`/${perm.path.replace(/^\/+/, '')}`}
                            </Typography>
                        )}

                        {rights.length > 0 && (
                            <>
                                <Divider sx={{ my: 0.75 }} />
                                <Box 
                                    display="flex" 
                                    flexWrap="wrap" 
                                    gap={0.5} 
                                    sx={{ pl: 1.4 }}
                                >
                                    {rights.map((r) => {
                                        const checked = selectedRights?.[permId]?.[r.rightName] || false;
                                        
                                        return (
                                            <FormControlLabel
                                                key={r.rightName}
                                                control={
                                                    <Checkbox
                                                        size="small"
                                                        checked={checked}
                                                        onChange={(e) => toggleRight(permId, r.rightName, e.target.checked)}
                                                        sx={{ p: 0.5 }}
                                                    />
                                                }
                                                label={
                                                    <Typography variant="caption">
                                                        {r.rightName}
                                                    </Typography>
                                                }
                                                sx={{ mr: 1 }}
                                            />
                                        );
                                    })}
                                </Box>
                            </>
                        )}
                    </Box>
                );
            })}
        </Box>
    );
};

export default SetPermissionList;
